import type { Schemas } from '@ers/sdk';
import { type QueryKey, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useCallback, useMemo } from 'react';
import { api, call, ifMatch } from '../../api/client';
import { qk } from '../../api/keys';
import { useProjects } from '../../api/projects';
import { useSession } from '../../app/session';
import type { ApiBody, ApiQuery } from '../../components/data/apiTypes';
import { useCursorQuery } from '../../components/data/useCursorQuery';

/*
 * Модуль «Автоматизація» (М5): правила «тригер → умова → дія», умови переходів етапів, шаблони підзадач
 * і чек-листів, форми з розгалуженням, повторювані роботи, пакети налаштувань, таймери строків реакції,
 * запити дій інших модулів. Ключі кешу — під префіксом ['automation', <розділ>], щоб подія потоку
 * «automation.<розділ>.*» оновлювала лише свій розділ. Права остаточно перевіряє сервер.
 */

// ——— Типи з контракту ———

export type Catalog = Schemas['AutomationCatalog'];
export type Rule = Schemas['AutomationRule'];
export type RuleInput = Schemas['AutomationRuleCreateInput'];
export type RulePatch = Schemas['AutomationRuleUpdateInput'];
export type TriggerInput = Schemas['AutomationTriggerInput'];
export type ActionInput = Schemas['AutomationActionInput'];
export type ConditionGroupInput = Schemas['AutomationConditionGroupInput'];
export type ConditionInput = Schemas['AutomationConditionInput'];
export type ConditionSubgroupInput = Schemas['AutomationConditionSubgroupInput'];
export type AssigneeInput = Schemas['AutomationAssigneeInput'];
export type RecipientsInput = Schemas['AutomationRecipientsInput'];
export type ScheduleInput = Schemas['AutomationScheduleInput'];
export type DryRunResult = Schemas['AutomationDryRunResult'];
export type RuleRun = Schemas['AutomationRuleRun'];
export type ActionRequest = Schemas['AutomationActionRequest'];

export type Gate = Schemas['AutomationGate'];
export type GateInput = Schemas['AutomationGateCreateInput'];
export type GatePatch = Schemas['AutomationGateUpdateInput'];
export type GateConditionsInput = Schemas['AutomationGateConditionsInput'];
export type GateQuestionInput = Schemas['AutomationGateQuestionInput'];
export type CommentFormulaInput = Schemas['AutomationCommentFormulaInput'];
export type TransitionTemplate = Schemas['AutomationTransitionTemplate'];
export type TransitionEvaluation = Schemas['AutomationTransitionEvaluation'];
export type TransitionLog = Schemas['AutomationTransitionLog'];

export type TaskTemplate = Schemas['AutomationTaskTemplate'];
export type TaskTemplateInput = Schemas['AutomationTaskTemplateCreateInput'];
export type TemplateItemInput = Schemas['AutomationTemplateItemInput'];
export type ChecklistItem = Schemas['AutomationChecklistItem'];

export type FormDef = Schemas['AutomationForm'];
export type FormInput = Schemas['AutomationFormCreateInput'];
export type FormPatch = Schemas['AutomationFormUpdateInput'];
export type FormFieldInput = Schemas['AutomationFormFieldInput'];
export type FormConditionInput = Schemas['AutomationFormConditionInput'];
export type FormTemplate = Schemas['AutomationFormTemplate'];
export type FormSubmission = Schemas['AutomationFormSubmission'];

export type Recurrence = Schemas['AutomationRecurrence'];
export type RecurrenceInput = Schemas['AutomationRecurrenceCreateInput'];
export type RecurrencePatch = Schemas['AutomationRecurrenceUpdateInput'];
export type RecurrenceTaskInput = Schemas['AutomationRecurrenceTaskInput'];
export type RecurrenceRun = Schemas['AutomationRecurrenceRun'];

export type AutomationPackage = Schemas['AutomationPackage'];
export type PackageVersion = Schemas['AutomationPackageVersion'];
export type PackageContent = Schemas['AutomationPackageContent'];
export type PackageContentInput = Schemas['AutomationPackageContentInput'];
export type PackageDiff = Schemas['AutomationPackageProjectDiff'];
export type PackageDiffItem = Schemas['AutomationPackageDiffItem'];
export type PackageApplication = Schemas['AutomationPackageApplication'];

export type SlaDefinition = Schemas['AutomationSlaDefinition'];
export type SlaInput = Schemas['AutomationSlaCreateInput'];
export type SlaPatch = Schemas['AutomationSlaUpdateInput'];
export type SlaDurationInput = Schemas['AutomationSlaDurationInput'];
export type SlaLevelInput = Schemas['AutomationSlaLevelInput'];
export type SlaOverrideInput = Schemas['AutomationSlaOverrideInput'];
export type SlaTimer = Schemas['AutomationSlaTimer'];
export type SlaReport = Schemas['AutomationSlaReport'];

/** Дані модуля в задачі: task.ext.automation (apps/api/src/modules/automation/extensions.ts). */
export type AutomationTaskExt = {
  checklist?: { total: number; done: number; requiredOpen: number };
  sla?: { timerId: string; definitionId: string; name: string; state: 'running' | 'paused'; dueAt: string; breached: boolean }[];
};

/** Безпечне читання task.ext.automation (сервер може бути новішим за застосунок). */
export function readTaskExt(ext: unknown): AutomationTaskExt | null {
  if (!ext || typeof ext !== 'object') return null;
  const e = ext as Record<string, unknown>;
  const out: AutomationTaskExt = {};
  const c = e.checklist as Record<string, unknown> | undefined;
  if (c && typeof c.total === 'number' && typeof c.done === 'number') out.checklist = { total: c.total, done: c.done, requiredOpen: typeof c.requiredOpen === 'number' ? c.requiredOpen : 0 };
  if (Array.isArray(e.sla))
    out.sla = e.sla
      .filter((t): t is Record<string, unknown> => !!t && typeof t === 'object' && typeof (t as Record<string, unknown>).dueAt === 'string')
      .map((t) => ({
        timerId: String(t.timerId ?? ''),
        definitionId: String(t.definitionId ?? ''),
        name: String(t.name ?? 'Строк реакції'),
        state: t.state === 'paused' ? 'paused' : 'running',
        dueAt: String(t.dueAt),
        breached: t.breached === true,
      }));
  return out.checklist || out.sla?.length ? out : null;
}

// ——— Ключі кешу ———

export const autoKeys = {
  all: ['automation'] as const,
  catalog: ['automation', 'catalog'] as const,
  rules: ['automation', 'rules'] as const,
  ruleList: (q: object) => ['automation', 'rules', 'list', q] as const,
  rule: (id: string) => ['automation', 'rules', 'item', id] as const,
  ruleRuns: (id: string, q: object) => ['automation', 'rules', 'runs', id, q] as const,
  runs: ['automation', 'runs'] as const,
  taskRuns: (taskId: string, q: object) => ['automation', 'runs', taskId, q] as const,
  requests: ['automation', 'requests'] as const,
  requestList: (q: object) => ['automation', 'requests', q] as const,
  gates: ['automation', 'gates'] as const,
  gateTemplates: ['automation', 'gates', 'templates'] as const,
  gateList: (projectId: string) => ['automation', 'gates', 'list', projectId] as const,
  transitions: (taskId: string) => ['automation', 'gates', 'log', taskId] as const,
  templates: ['automation', 'templates'] as const,
  templateList: (q: object) => ['automation', 'templates', q] as const,
  checklist: (taskId: string) => ['automation', 'checklist', taskId] as const,
  forms: ['automation', 'forms'] as const,
  formTemplates: ['automation', 'forms', 'templates'] as const,
  formList: (q: object) => ['automation', 'forms', 'list', q] as const,
  form: (id: string) => ['automation', 'forms', 'item', id] as const,
  submissions: (id: string, q: object) => ['automation', 'forms', 'submissions', id, q] as const,
  recurrences: ['automation', 'recurrences'] as const,
  recurrenceList: (q: object) => ['automation', 'recurrences', 'list', q] as const,
  recurrence: (id: string) => ['automation', 'recurrences', 'item', id] as const,
  recurrenceHistory: (id: string) => ['automation', 'recurrences', 'history', id] as const,
  packages: ['automation', 'packages'] as const,
  packageList: (q: object) => ['automation', 'packages', 'list', q] as const,
  package: (id: string) => ['automation', 'packages', 'item', id] as const,
  packageVersion: (id: string, v: number) => ['automation', 'packages', 'version', id, v] as const,
  packageApps: (id: string) => ['automation', 'packages', 'applications', id] as const,
  sla: ['automation', 'sla'] as const,
  slaDefs: (q: object) => ['automation', 'sla', 'defs', q] as const,
  slaTimers: (q: object) => ['automation', 'sla', 'timers', q] as const,
  slaReport: (q: object) => ['automation', 'sla', 'report', q] as const,
};

/** Мутація з інвалідацією ключів після успіху. */
function useAutoMutation<V, R>(fn: (v: V) => Promise<R>, keys: (v: V, r: R) => QueryKey[]) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: fn,
    onSuccess: (r, v) => {
      for (const k of keys(v, r)) void qc.invalidateQueries({ queryKey: k });
    },
  });
}

const vh = (version?: number) => (version != null ? ifMatch(version) : undefined);

// ——— Права ———

/**
 * Хто може налаштовувати: адміністратор організації (усе) або адміністратор проєкту (правила, форми,
 * умови переходів, таймери, повторювані роботи свого проєкту). Потрібна область projects:write.
 */
export function useConfigAccess() {
  const { can, isAdmin } = useSession();
  const projects = useProjects();
  const write = can('projects:write');
  const adminProjects = useMemo(
    () => (projects.data ?? []).filter((p) => !p.archivedAt && (isAdmin || p.myRole === 'admin')).map((p) => p.id),
    [projects.data, isAdmin],
  );
  const canConfigure = useCallback(
    (projectId: string | null | undefined) => {
      if (!write) return false;
      if (isAdmin) return true;
      if (!projectId) return false;
      return adminProjects.includes(projectId);
    },
    [write, isAdmin, adminProjects],
  );
  return { write, isAdmin, orgConfig: write && isAdmin, adminProjects, canConfigure, anyConfig: write && (isAdmin || adminProjects.length > 0) };
}

// ——— Каталог ———

export function useCatalog() {
  return useQuery({ queryKey: autoKeys.catalog, staleTime: 30 * 60_000, queryFn: () => call(api.GET('/api/v1/automation/catalog')) });
}

// ——— Правила ———

type RulesQuery = ApiQuery<'/api/v1/automation/rules', 'get'>;

export function useRules(q: Omit<RulesQuery, 'cursor' | 'limit'>, opts: { enabled?: boolean } = {}) {
  return useCursorQuery(
    autoKeys.ruleList(q),
    (cursor) => call(api.GET('/api/v1/automation/rules', { params: { query: { ...q, cursor, limit: 100 } } })),
    opts,
  );
}

export function useRule(id: string | null | undefined) {
  return useQuery({
    queryKey: autoKeys.rule(id ?? ''),
    enabled: !!id,
    queryFn: () => call(api.GET('/api/v1/automation/rules/{id}', { params: { path: { id: id! } } })),
  });
}

export function useCreateRule() {
  return useAutoMutation((body: ApiBody<'/api/v1/automation/rules', 'post'>) => call(api.POST('/api/v1/automation/rules', { body })), () => [autoKeys.rules]);
}

export function useUpdateRule() {
  return useAutoMutation(
    ({ id, body, version }: { id: string; body: RulePatch; version?: number }) =>
      call(api.PATCH('/api/v1/automation/rules/{id}', { params: { path: { id } }, body, headers: vh(version) })),
    () => [autoKeys.rules],
  );
}

export function useArchiveRule() {
  return useAutoMutation((id: string) => call(api.DELETE('/api/v1/automation/rules/{id}', { params: { path: { id } } })), () => [autoKeys.rules]);
}

export function usePreviewRule() {
  return useMutation({ mutationFn: (body: ApiBody<'/api/v1/automation/rules/preview', 'post'>) => call(api.POST('/api/v1/automation/rules/preview', { body })) });
}

export function useDryRunRule() {
  return useAutoMutation(
    ({ id, taskId }: { id: string; taskId?: string }) => call(api.POST('/api/v1/automation/rules/{id}/dry-run', { params: { path: { id } }, body: { taskId } })),
    (v) => [autoKeys.rule(v.id), ['automation', 'rules', 'runs', v.id], autoKeys.runs],
  );
}

export function useRunRule() {
  const qc = useQueryClient();
  return useAutoMutation(
    ({ id, taskId }: { id: string; taskId?: string }) => call(api.POST('/api/v1/automation/rules/{id}/run', { params: { path: { id } }, body: { taskId } })),
    (v) => {
      if (v.taskId) {
        void qc.invalidateQueries({ queryKey: qk.task(v.taskId) });
        void qc.invalidateQueries({ queryKey: qk.tasksAll });
      }
      return [autoKeys.rules, autoKeys.runs];
    },
  );
}

export function useRotateWebhookSecret() {
  return useAutoMutation((id: string) => call(api.POST('/api/v1/automation/rules/{id}/webhook-secret', { params: { path: { id } } })), (id) => [autoKeys.rule(id)]);
}

type RunsQuery = ApiQuery<'/api/v1/automation/rules/{id}/runs', 'get'>;

export function useRuleRuns(id: string | null | undefined, q: Omit<RunsQuery, 'cursor' | 'limit'>) {
  return useCursorQuery(
    autoKeys.ruleRuns(id ?? '', q),
    (cursor) => call(api.GET('/api/v1/automation/rules/{id}/runs', { params: { path: { id: id! }, query: { ...q, cursor, limit: 30 } } })),
    { enabled: !!id },
  );
}

export function useTaskRuns(taskId: string | null | undefined, q: Omit<RunsQuery, 'cursor' | 'limit'>) {
  return useCursorQuery(
    autoKeys.taskRuns(taskId ?? '', q),
    (cursor) => call(api.GET('/api/v1/automation/runs', { params: { query: { ...q, taskId: taskId!, cursor, limit: 30 } } })),
    { enabled: !!taskId },
  );
}

// ——— Запити дій ———

type RequestsQuery = ApiQuery<'/api/v1/automation/action-requests', 'get'>;

export function useActionRequests(q: Omit<RequestsQuery, 'cursor' | 'limit'>) {
  return useCursorQuery(autoKeys.requestList(q), (cursor) => call(api.GET('/api/v1/automation/action-requests', { params: { query: { ...q, cursor, limit: 50 } } })));
}

export function useReportRequestResult() {
  return useAutoMutation(
    ({ id, body }: { id: string; body: ApiBody<'/api/v1/automation/action-requests/{id}/result', 'post'> }) =>
      call(api.POST('/api/v1/automation/action-requests/{id}/result', { params: { path: { id } }, body })),
    () => [autoKeys.requests],
  );
}

// ——— Умови переходів ———

export function useTransitionTemplates() {
  return useQuery({
    queryKey: autoKeys.gateTemplates,
    staleTime: 30 * 60_000,
    queryFn: async () => (await call(api.GET('/api/v1/automation/transition-templates'))).data,
  });
}

export function useGates(projectId: string | null | undefined) {
  return useQuery({
    queryKey: autoKeys.gateList(projectId ?? ''),
    enabled: !!projectId,
    queryFn: async () => (await call(api.GET('/api/v1/automation/transition-gates', { params: { query: { projectId: projectId! } } }))).data,
  });
}

export function useCreateGate() {
  return useAutoMutation((body: GateInput) => call(api.POST('/api/v1/automation/transition-gates', { body })), () => [autoKeys.gates]);
}

export function useUpdateGate() {
  return useAutoMutation(
    ({ id, body, version }: { id: string; body: GatePatch; version?: number }) =>
      call(api.PATCH('/api/v1/automation/transition-gates/{id}', { params: { path: { id } }, body, headers: vh(version) })),
    () => [autoKeys.gates],
  );
}

export function useArchiveGate() {
  return useAutoMutation((id: string) => call(api.DELETE('/api/v1/automation/transition-gates/{id}', { params: { path: { id } } })), () => [autoKeys.gates]);
}

export function useTransitionCheck() {
  return useMutation({
    mutationFn: ({ taskId, body }: { taskId: string; body: ApiBody<'/api/v1/automation/tasks/{id}/transition-check', 'post'> }) =>
      call(api.POST('/api/v1/automation/tasks/{id}/transition-check', { params: { path: { id: taskId } }, body })),
  });
}

export function useTaskTransitions(taskId: string | null | undefined) {
  return useQuery({
    queryKey: autoKeys.transitions(taskId ?? ''),
    enabled: !!taskId,
    queryFn: async () => (await call(api.GET('/api/v1/automation/tasks/{id}/transitions', { params: { path: { id: taskId! } } }))).data,
  });
}

// ——— Шаблони й чек-листи ———

export function useTaskTemplates(q: ApiQuery<'/api/v1/automation/task-templates', 'get'> = {}, opts: { enabled?: boolean } = {}) {
  return useQuery({
    queryKey: autoKeys.templateList(q),
    enabled: opts.enabled ?? true,
    queryFn: async () => (await call(api.GET('/api/v1/automation/task-templates', { params: { query: q } }))).data,
  });
}

export function useCreateTemplate() {
  return useAutoMutation((body: TaskTemplateInput) => call(api.POST('/api/v1/automation/task-templates', { body })), () => [autoKeys.templates]);
}

export function useUpdateTemplate() {
  return useAutoMutation(
    ({ id, body, version }: { id: string; body: ApiBody<'/api/v1/automation/task-templates/{id}', 'patch'>; version?: number }) =>
      call(api.PATCH('/api/v1/automation/task-templates/{id}', { params: { path: { id } }, body, headers: vh(version) })),
    () => [autoKeys.templates],
  );
}

export function useArchiveTemplate() {
  return useAutoMutation((id: string) => call(api.DELETE('/api/v1/automation/task-templates/{id}', { params: { path: { id } } })), () => [autoKeys.templates]);
}

/** Після змін чек-листа оновлюємо й задачу (task.ext.automation — лічильник на картці й у рядках). */
function taskKeys(taskId: string): QueryKey[] {
  return [autoKeys.checklist(taskId), qk.task(taskId), qk.tasksAll];
}

export function useApplyTemplate() {
  return useAutoMutation(
    ({ taskId, templateId }: { taskId: string; templateId: string }) =>
      call(api.POST('/api/v1/automation/tasks/{id}/apply-template', { params: { path: { id: taskId } }, body: { templateId } })),
    (v) => [...taskKeys(v.taskId), qk.taskPart(v.taskId, 'subtasks')],
  );
}

export function useChecklist(taskId: string | null | undefined) {
  return useQuery({
    queryKey: autoKeys.checklist(taskId ?? ''),
    enabled: !!taskId,
    queryFn: async () => (await call(api.GET('/api/v1/automation/tasks/{id}/checklist', { params: { path: { id: taskId! } } }))).data,
  });
}

export function useAddChecklistItem(taskId: string) {
  return useAutoMutation(
    (body: ApiBody<'/api/v1/automation/tasks/{id}/checklist', 'post'>) => call(api.POST('/api/v1/automation/tasks/{id}/checklist', { params: { path: { id: taskId } }, body })),
    () => taskKeys(taskId),
  );
}

export function useUpdateChecklistItem(taskId: string) {
  return useAutoMutation(
    ({ id, body }: { id: string; body: ApiBody<'/api/v1/automation/checklist-items/{id}', 'patch'> }) =>
      call(api.PATCH('/api/v1/automation/checklist-items/{id}', { params: { path: { id } }, body })),
    () => taskKeys(taskId),
  );
}

export function useRemoveChecklistItem(taskId: string) {
  return useAutoMutation((id: string) => call(api.DELETE('/api/v1/automation/checklist-items/{id}', { params: { path: { id } } })), () => taskKeys(taskId));
}

// ——— Форми ———

export function useFormTemplates() {
  return useQuery({ queryKey: autoKeys.formTemplates, staleTime: 30 * 60_000, queryFn: async () => (await call(api.GET('/api/v1/automation/form-templates'))).data });
}

export function useForms(q: ApiQuery<'/api/v1/automation/forms', 'get'> = {}, opts: { enabled?: boolean } = {}) {
  return useQuery({
    queryKey: autoKeys.formList(q),
    enabled: opts.enabled ?? true,
    queryFn: async () => (await call(api.GET('/api/v1/automation/forms', { params: { query: q } }))).data,
  });
}

export function useForm(id: string | null | undefined) {
  return useQuery({ queryKey: autoKeys.form(id ?? ''), enabled: !!id, queryFn: () => call(api.GET('/api/v1/automation/forms/{id}', { params: { path: { id: id! } } })) });
}

export function useCreateForm() {
  return useAutoMutation((body: FormInput) => call(api.POST('/api/v1/automation/forms', { body })), () => [autoKeys.forms]);
}

export function useUpdateForm() {
  return useAutoMutation(
    ({ id, body, version }: { id: string; body: FormPatch; version?: number }) =>
      call(api.PATCH('/api/v1/automation/forms/{id}', { params: { path: { id } }, body, headers: vh(version) })),
    () => [autoKeys.forms],
  );
}

export function useArchiveForm() {
  return useAutoMutation((id: string) => call(api.DELETE('/api/v1/automation/forms/{id}', { params: { path: { id } } })), () => [autoKeys.forms]);
}

export function useFormPublicLink() {
  return useAutoMutation(
    ({ id, enabled, rotate }: { id: string; enabled: boolean; rotate?: boolean }) =>
      call(api.POST('/api/v1/automation/forms/{id}/public-link', { params: { path: { id } }, body: { enabled, rotate } })),
    () => [autoKeys.forms],
  );
}

export function useSubmitForm() {
  return useAutoMutation(
    ({ id, body }: { id: string; body: ApiBody<'/api/v1/automation/forms/{id}/submissions', 'post'> }) =>
      call(api.POST('/api/v1/automation/forms/{id}/submissions', { params: { path: { id } }, body })),
    () => [autoKeys.forms, qk.tasksAll],
  );
}

type SubmissionsQuery = ApiQuery<'/api/v1/automation/forms/{id}/submissions', 'get'>;

export function useFormSubmissions(id: string | null | undefined, q: Omit<SubmissionsQuery, 'cursor' | 'limit'>) {
  return useCursorQuery(
    autoKeys.submissions(id ?? '', q),
    (cursor) => call(api.GET('/api/v1/automation/forms/{id}/submissions', { params: { path: { id: id! }, query: { ...q, cursor, limit: 30 } } })),
    { enabled: !!id },
  );
}

// ——— Повторювані роботи ———

export function useRecurrences(q: ApiQuery<'/api/v1/automation/recurrences', 'get'> = {}) {
  return useQuery({ queryKey: autoKeys.recurrenceList(q), queryFn: async () => (await call(api.GET('/api/v1/automation/recurrences', { params: { query: q } }))).data });
}

export function useRecurrence(id: string | null | undefined) {
  return useQuery({
    queryKey: autoKeys.recurrence(id ?? ''),
    enabled: !!id,
    queryFn: () => call(api.GET('/api/v1/automation/recurrences/{id}', { params: { path: { id: id! } } })),
  });
}

export function useCreateRecurrence() {
  return useAutoMutation((body: RecurrenceInput) => call(api.POST('/api/v1/automation/recurrences', { body })), () => [autoKeys.recurrences]);
}

export function useUpdateRecurrence() {
  return useAutoMutation(
    ({ id, body, version }: { id: string; body: RecurrencePatch; version?: number }) =>
      call(api.PATCH('/api/v1/automation/recurrences/{id}', { params: { path: { id } }, body, headers: vh(version) })),
    () => [autoKeys.recurrences],
  );
}

export function useArchiveRecurrence() {
  return useAutoMutation((id: string) => call(api.DELETE('/api/v1/automation/recurrences/{id}', { params: { path: { id } } })), () => [autoKeys.recurrences]);
}

export function useRecurrenceHistory(id: string | null | undefined) {
  return useQuery({
    queryKey: autoKeys.recurrenceHistory(id ?? ''),
    enabled: !!id,
    queryFn: async () => (await call(api.GET('/api/v1/automation/recurrences/{id}/history', { params: { path: { id: id! } } }))).data,
  });
}

export function useRunRecurrence() {
  return useAutoMutation((id: string) => call(api.POST('/api/v1/automation/recurrences/{id}/run', { params: { path: { id } } })), () => [autoKeys.recurrences, qk.tasksAll]);
}

// ——— Пакети налаштувань ———

export function usePackages(q: ApiQuery<'/api/v1/automation/packages', 'get'> = {}) {
  return useQuery({ queryKey: autoKeys.packageList(q), queryFn: async () => (await call(api.GET('/api/v1/automation/packages', { params: { query: q } }))).data });
}

export function usePackage(id: string | null | undefined) {
  return useQuery({ queryKey: autoKeys.package(id ?? ''), enabled: !!id, queryFn: () => call(api.GET('/api/v1/automation/packages/{id}', { params: { path: { id: id! } } })) });
}

export function useCreatePackage() {
  return useAutoMutation((body: ApiBody<'/api/v1/automation/packages', 'post'>) => call(api.POST('/api/v1/automation/packages', { body })), () => [autoKeys.packages]);
}

export function useUpdatePackage() {
  return useAutoMutation(
    ({ id, body }: { id: string; body: ApiBody<'/api/v1/automation/packages/{id}', 'patch'> }) =>
      call(api.PATCH('/api/v1/automation/packages/{id}', { params: { path: { id } }, body })),
    () => [autoKeys.packages],
  );
}

export function usePublishVersion() {
  return useAutoMutation(
    ({ id, body }: { id: string; body: ApiBody<'/api/v1/automation/packages/{id}/versions', 'post'> }) =>
      call(api.POST('/api/v1/automation/packages/{id}/versions', { params: { path: { id } }, body })),
    () => [autoKeys.packages],
  );
}

export function usePackageVersion(id: string | null | undefined, version: number | null | undefined) {
  return useQuery({
    queryKey: autoKeys.packageVersion(id ?? '', version ?? 0),
    enabled: !!id && !!version,
    staleTime: Infinity,
    queryFn: () => call(api.GET('/api/v1/automation/packages/{id}/versions/{version}', { params: { path: { id: id!, version: version! } } })),
  });
}

export function usePreviewPackage() {
  return useMutation({
    mutationFn: ({ id, body }: { id: string; body: ApiBody<'/api/v1/automation/packages/{id}/preview', 'post'> }) =>
      call(api.POST('/api/v1/automation/packages/{id}/preview', { params: { path: { id } }, body })),
  });
}

export function useApplyPackage() {
  return useAutoMutation(
    ({ id, body }: { id: string; body: ApiBody<'/api/v1/automation/packages/{id}/apply', 'post'> }) =>
      call(api.POST('/api/v1/automation/packages/{id}/apply', { params: { path: { id } }, body })),
    () => [autoKeys.all],
  );
}

export function usePackageApplications(id: string | null | undefined, enabled = true) {
  return useQuery({
    queryKey: autoKeys.packageApps(id ?? ''),
    enabled: !!id && enabled,
    retry: false,
    queryFn: async () => (await call(api.GET('/api/v1/automation/packages/{id}/applications', { params: { path: { id: id! } } }))).data,
  });
}

// ——— Строки реакції ———

export function useSlaDefinitions(q: ApiQuery<'/api/v1/automation/sla-definitions', 'get'> = {}) {
  return useQuery({ queryKey: autoKeys.slaDefs(q), queryFn: async () => (await call(api.GET('/api/v1/automation/sla-definitions', { params: { query: q } }))).data });
}

export function useCreateSla() {
  return useAutoMutation((body: SlaInput) => call(api.POST('/api/v1/automation/sla-definitions', { body })), () => [autoKeys.sla]);
}

export function useUpdateSla() {
  return useAutoMutation(
    ({ id, body, version }: { id: string; body: SlaPatch; version?: number }) =>
      call(api.PATCH('/api/v1/automation/sla-definitions/{id}', { params: { path: { id } }, body, headers: vh(version) })),
    () => [autoKeys.sla],
  );
}

export function useArchiveSla() {
  return useAutoMutation((id: string) => call(api.DELETE('/api/v1/automation/sla-definitions/{id}', { params: { path: { id } } })), () => [autoKeys.sla]);
}

type TimersQuery = ApiQuery<'/api/v1/automation/sla-timers', 'get'>;

export function useSlaTimers(q: Omit<TimersQuery, 'cursor' | 'limit'>) {
  return useCursorQuery(autoKeys.slaTimers(q), (cursor) => call(api.GET('/api/v1/automation/sla-timers', { params: { query: { ...q, cursor, limit: 50 } } })));
}

export function useSlaReport(q: ApiQuery<'/api/v1/automation/sla-report', 'get'>) {
  return useQuery({ queryKey: autoKeys.slaReport(q), queryFn: () => call(api.GET('/api/v1/automation/sla-report', { params: { query: q } })) });
}
